import {RIF} from '../../lib'
import {selectTheme} from '../../store'
import {QuestionnaireInput, ButtonCancel, QuestionnaireInputInitialValue} from '..'
import {DraggingIcon, DragIcon} from '../../asset/image'
import {QuestionnaireTempQuestion} from '../../model'
import {QuestionnaireQuestionType} from '../../shared/db'
import {useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {SkipIcon} from '../../lib/svg/icon/skip'
import {EyeIcon} from '../../lib/svg/icon/eye'

export const QuestionnaireOption = (props: {
  isEditing: boolean
  index: number
  question: QuestionnaireTempQuestion
  option: {
    value: string
    name: string
  }
  editOptionName: (optionIndex: number, name: string) => void
  deleteOption: (optionIndex: number) => void
  showDeleteButton: boolean
  viewOnly: boolean
  skipLogicText?: string
  displayLogicText?: string[]
}) => {
  const {color, fontSize, fontWeight} = selectTheme()
  const {
    isEditing,
    index,
    question,
    option,
    editOptionName,
    deleteOption,
    showDeleteButton,
    viewOnly,
    skipLogicText,
    displayLogicText,
  } = props
  const {attributes, listeners, setNodeRef, transform, setActivatorNodeRef} = useSortable({
    id: option.value,
  })
  const style = {transform: CSS.Translate.toString(transform)}

  const handleChangeOptionName = (value: string) => {
    editOptionName(index, value)
  }

  const showSelectionIndicator = !(
    question?.type === QuestionnaireQuestionType.Radio && question?.inputConfig?.showSlider
  )

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{
        ...style,
        display: 'flex',
        // alignItems: 'center',
        gap: '8px',
        minHeight: '40px',
        boxSizing: 'border-box',
        position: 'relative',
      }}>
      {/* drag handle */}
      {RIF(
        !viewOnly && isEditing,
        <div
          ref={setActivatorNodeRef}
          {...listeners}
          css={{
            width: '8px',
            alignSelf: 'stretch',
            marginRight: '8px',
            backgroundImage: `url(${DragIcon})`,
            backgroundSize: '100% auto',
            backgroundPosition: '0 12px',
            backgroundRepeat: 'no-repeat',
            cursor: 'grab',
            ':hover': {
              backgroundImage: `url(${DraggingIcon})`,
            },
          }}
        />,
      )}

      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          flex: 1,
        }}>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            height: isEditing ? '40px' : 'auto',
          }}>
          {/* check box */}
          {showSelectionIndicator && (
            <div
              css={{
                width: '16px',
                minWidth: '16px',
                height: '16px',
                minHeight: '16px',
                borderRadius: question?.type === QuestionnaireQuestionType.Radio ? '50%' : '2px',
                border: `1px solid ${color.grey_400}`,
              }}
            />
          )}
          {isEditing && (
            <>
              <QuestionnaireInput
                placeholder={QuestionnaireInputInitialValue.QuestionOption}
                onChangeAction={handleChangeOptionName}
                defaultValue={option?.name}
                initialValue={QuestionnaireInputInitialValue.QuestionOption}
                maxLength={250}
              />
              {RIF(showDeleteButton, <ButtonCancel onClick={() => deleteOption(index)} />)}
            </>
          )}
          {!isEditing && <div>{option?.name}</div>}
        </div>
        {skipLogicText && (
          <div
            css={{
              width: 'calc(100% - 56px)',
              padding: '4px 16px',
              marginLeft: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              borderRadius: '5px',
              backgroundColor: color.tag.purple.light,
            }}>
            <SkipIcon width={14} fill={color.tag.purple.dark} />
            <span css={{fontSize: fontSize.h8, fontWeight: fontWeight.bold, color: color.tag.purple.dark}}>
              {skipLogicText}
            </span>
          </div>
        )}
        {displayLogicText &&
          displayLogicText.length > 0 &&
          displayLogicText.map((text) => (
            <div
              css={{
                width: 'calc(100% - 56px)',
                padding: '4px 16px',
                marginLeft: '24px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                borderRadius: '5px',
                backgroundColor: color.surface.nested,
              }}>
              <EyeIcon width={14} fill={color.textIcon.secondary} />
              <span css={{fontSize: fontSize.h8, fontWeight: fontWeight.bold, color: color.textIcon.secondary}}>
                {text}
              </span>
            </div>
          ))}
      </div>
    </div>
  )
}
